import React from 'react'
import {
	Box, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow, TableCell,
	Grid, ToggleButton, ToggleButtonGroup
} from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'


const DATES = ['15 Mar', '22 Mar', '01 Apr', '10 Apr']

const DEMO_ROWS = [
	{
		name: 'DrivePro',
		prices: [
			{ price: '€48.30', prev: '€46.80', cheapest: false, expensive: false, evo: 'increase', diff: 3.2 },
			{ price: '€52.10', prev: '€54.90', cheapest: false, expensive: false, evo: 'decrease', diff: 5.1 },
			{ price: '€45.70', prev: '€45.70', cheapest: true, expensive: false, evo: null, diff: null },
			{ price: '€58.20', prev: '€55.10', cheapest: false, expensive: false, evo: 'increase', diff: 5.6 },
		],
	},
	{
		name: 'FleetMax',
		prices: [
			{ price: '€42.50', prev: '€44.80', cheapest: true, expensive: false, evo: 'decrease', diff: 5.1 },
			{ price: '€46.90', prev: '€48.20', cheapest: true, expensive: false, evo: 'decrease', diff: 2.7 },
			{ price: '€48.30', prev: '€47.10', cheapest: false, expensive: false, evo: 'increase', diff: 2.5 },
			{ price: '€51.40', prev: '€53.80', cheapest: true, expensive: false, evo: 'decrease', diff: 4.5 },
		],
	},
	{
		name: 'CarFirst',
		prices: [
			{ price: '€55.10', prev: '€50.20', cheapest: false, expensive: true, evo: 'increase', diff: 9.8 },
			{ price: '€58.40', prev: '€55.90', cheapest: false, expensive: true, evo: 'increase', diff: 4.5 },
			{ price: '€52.80', prev: '€54.30', cheapest: false, expensive: false, evo: 'decrease', diff: 2.8 },
			{ price: '€62.10', prev: '€58.70', cheapest: false, expensive: true, evo: 'increase', diff: 5.8 },
		],
	},
	{
		name: 'AutoRent',
		prices: [
			{ price: '€49.80', prev: '€49.80', cheapest: false, expensive: false, evo: null, diff: null },
			{ price: '€53.20', prev: '€51.40', cheapest: false, expensive: false, evo: 'increase', diff: 3.5 },
			{ price: '€47.90', prev: '€50.10', cheapest: false, expensive: false, evo: 'decrease', diff: 4.4 },
			{ price: '€56.70', prev: '€56.70', cheapest: false, expensive: false, evo: null, diff: null },
		],
	},
	{
		name: 'SpeedCar',
		prices: [
			{ price: '€51.60', prev: '€48.90', cheapest: false, expensive: false, evo: 'increase', diff: 5.5 },
			{ price: '€55.80', prev: '€57.10', cheapest: false, expensive: false, evo: 'decrease', diff: 2.3 },
			{ price: '€60.20', prev: '€56.40', cheapest: false, expensive: true, evo: 'increase', diff: 6.7 },
			{ price: '€54.30', prev: '€52.10', cheapest: false, expensive: false, evo: 'increase', diff: 4.2 },
		],
	},
]

function PriceCell({ data }) {
	const borderColor = data.cheapest ? 'success.main' : data.expensive ? 'error.main' : 'secondary.light'
	const bgColor = data.cheapest ? 'rgba(46,125,50,0.08)' : data.expensive ? 'rgba(211,47,47,0.08)' : 'transparent'

	return <Box sx={{ border: 2, borderColor, backgroundColor: bgColor, borderRadius: 1, p: 0.8, minWidth: 110 }}>
		<Typography sx={{ fontWeight: 700, fontSize: '0.82rem', textAlign: 'center' }}>{data.price}</Typography>
		{data.prev && <Typography sx={{ fontSize: '0.62rem', color: 'secondary.light', textAlign: 'center' }}>
			prev: {data.prev}
		</Typography>}
		{data.evo && <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3, mt: 0.3 }}>
			<IconSeeker
				icon={data.evo === 'increase' ? 'arrow-up' : 'arrow-down'}
				sx={{ fontSize: 11, color: data.evo === 'increase' ? 'error.main' : 'success.dark' }}
			/>
			<Typography sx={{ fontSize: '0.65rem', color: data.evo === 'increase' ? 'error.main' : 'success.dark', fontWeight: 600 }}>
				{data.diff}%
			</Typography>
		</Box>}
	</Box>
}

function ChartPlaceholder() {
	const series = [
		{ name: 'DrivePro', color: '#1976d2', points: [48, 52, 46, 58] },
		{ name: 'FleetMax', color: '#2e7d32', points: [42, 47, 48, 51] },
		{ name: 'CarFirst', color: '#e65100', points: [55, 58, 53, 62] },
		{ name: 'AutoRent', color: '#9c27b0', points: [50, 53, 48, 57] },
		{ name: 'SpeedCar', color: '#d32f2f', points: [52, 56, 60, 54] },
	]
	const max = 65, min = 40
	const range = max - min

	return <Card>
		<CardContent>
			<Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Chart</Typography>
			<Box sx={{ position: 'relative', height: 200, pl: 4, pr: 2 }}>
				{/* Y axis labels */}
				{[max, Math.round((max + min) / 2), min].map((val, i) => (
					<Typography key={i} sx={{
						position: 'absolute', left: 0, fontSize: '0.62rem', color: 'secondary.light',
						top: `${(1 - (val - min) / range) * 100}%`, transform: 'translateY(-50%)',
					}}>€{val}</Typography>
				))}
				{/* Grid lines */}
				{[0, 0.5, 1].map((pos, i) => (
					<Box key={i} sx={{
						position: 'absolute', left: 30, right: 0, top: `${pos * 100}%`,
						borderBottom: '1px dashed', borderColor: 'divider',
					}} />
				))}
				{/* SVG lines */}
				<svg width="100%" height="100%" viewBox="0 0 400 200" preserveAspectRatio="none" style={{ position: 'absolute', left: 30, top: 0, width: 'calc(100% - 45px)' }}>
					{series.map((s, si) => {
						const pts = s.points.map((p, i) => `${(i / 3) * 380 + 10},${(1 - (p - min) / range) * 190 + 5}`).join(' ')
						return <polyline key={si} points={pts} fill="none" stroke={s.color} strokeWidth="2.5" strokeLinejoin="round" />
					})}
				</svg>
			</Box>
			{/* X axis */}
			<Box sx={{ display: 'flex', justifyContent: 'space-around', pl: 4, mt: 1 }}>
				{DATES.map((d, i) => (
					<Typography key={i} sx={{ fontSize: '0.65rem', color: 'secondary.light' }}>{d}</Typography>
				))}
			</Box>
			{/* Legend */}
			<Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
				{series.map((s, i) => (
					<Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
						<Box sx={{ width: 14, height: 3, bgcolor: s.color, borderRadius: 1 }} />
						<Typography sx={{ fontSize: '0.68rem' }}>{s.name}</Typography>
					</Box>
				))}
			</Box>
		</CardContent>
	</Card>
}


export default function BIDemoData() {
	return <Grid container spacing={2}>
		{/* Table Actions */}
		<Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<ToggleButtonGroup size="small" value="min" exclusive>
					<ToggleButton value="min" sx={{ px: 2, py: 0.5, textTransform: 'none', fontSize: '0.72rem' }}>Min. Price</ToggleButton>
					<ToggleButton value="avg" sx={{ px: 2, py: 0.5, textTransform: 'none', fontSize: '0.72rem' }}>Avg. Price</ToggleButton>
					<ToggleButton value="max" sx={{ px: 2, py: 0.5, textTransform: 'none', fontSize: '0.72rem' }}>Max. Price</ToggleButton>
				</ToggleButtonGroup>
			</Box>
		</Grid>

		{/* Price Comparison Table */}
		<Grid item xs={12}>
			<Card>
				<CardContent sx={{ p: 2 }}>
					<Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Price Comparison</Typography>
					<Box sx={{ overflowX: 'auto' }}>
						<Table size="small">
							<TableHead>
								<TableRow sx={{ '& th': { bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.72rem', textAlign: 'center' } }}>
									<TableCell sx={{ position: 'sticky', left: 0, zIndex: 2, bgcolor: 'primary.main' }}>Provider</TableCell>
									{DATES.map(d => <TableCell key={d}>{d}</TableCell>)}
								</TableRow>
							</TableHead>
							<TableBody>
								{DEMO_ROWS.map((row, ri) => (
									<TableRow key={ri} sx={{ backgroundColor: ri % 2 === 0 ? '#fafafa' : '#f5f5f5' }}>
										<TableCell sx={{
											position: 'sticky', left: 0, zIndex: 1, fontWeight: 700,
											fontSize: '0.8rem', bgcolor: 'primary.main', color: 'white',
											borderBottom: 1, borderColor: 'primary.dark',
										}}>
											{row.name}
										</TableCell>
										{row.prices.map((p, pi) => (
											<TableCell key={pi} sx={{ textAlign: 'center', p: 0.8 }}>
												<PriceCell data={p} />
											</TableCell>
										))}
									</TableRow>
								))}
							</TableBody>
						</Table>
					</Box>
				</CardContent>
			</Card>
		</Grid>

		{/* Chart */}
		<Grid item xs={12}>
			<ChartPlaceholder />
		</Grid>
	</Grid>
}
